/*
 * Module for the topic label modal
 */

define([
  "backbone",
  "marionette",
  "jquery",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicLabelModal/topicLabelModalView",
  "modules/topicsSplit/topic/topicLabel/topicLabelModel",
  "modules/shared/touchPointLabelList/touchPointLabelListModule",
  "modules/shared/topicLabelList/topicLabelListModule",
], function (
  Backbone,
  Marionette,
  $,
  Messenger,
  TopicLabelModalView,
  TopicLabelModel,
  TouchPointLabelListModule,
  TopicLabelListModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "topicLabelModal";
      this.activeIntentId = options.intentId;
      this.topicLabels = options.topicLabels;
      this.touchPointLabels = options.touchPointLabels;
    },

    show: function () {
      this.topicLabels.backup();
      this.touchPointLabels.backup();
      this.hideEnglishLabel = false;
      this.layout = new TopicLabelModalView.topicLabelModalLayout();
      this.defaultTouchpointId = Messenger.reqres.request("default:touchpoint");
      this.standardTouchPointName = $.t("touchpoints.standardTouchPointName");
      this.standardTouchpointId = -100;

      this.layout.on("touchPoint:changed", this._changeTouchpoint, this);

      this.labelList = this.addSubModule(
        new TopicLabelListModule({
          region: this.layout.labelList,
          hideEnglishLabel: this.hideEnglishLabel,
          namespace: this.name,
        }),
      );

      this.layout.on("done", () => {
        this._prepareTouchPointLabels(this.touchPointLabels);
        Messenger.vent.trigger("show:saving:display");
        this.removeEmptyTopicAndTouchpointLabels()
          .then(() => {
            return this.topicLabels.saveCollectionPromise();
          })
          .then(() => this.touchPointLabels.saveCollectionPromise())
          .catch((e) => {
            console.error("Error saving topic and touchpoint labels", e);
            return Promise.reject(e);
          })
          .finally(() => {
            Messenger.vent.trigger("close:saving:display");
            Messenger.vent.trigger("close:modal");
          });
      });

      this.layout.on("cancel", () => {
        this.touchPointLabels.restore();
        this.topicLabels.restore();
        Messenger.vent.trigger("close:modal");
      });

      this.layout.on("show", () => this._showSubViews(this.topicLabels, false));

      const promises = [
        this.topicLabels.onSyncedPromise(),
        this.touchPointLabels.onSyncedPromise(),
      ];

      Promise.all(promises).then(() => {
        this.showRegion(this.layout);
      });
    },
    _changeTouchpoint: function () {
      this.selectedTouchPointId = this.layout.getTouchPoint();
      if (this.selectedTouchPointId === this.standardTouchpointId) {
        this.labelList = this.addSubModule(
          new TopicLabelListModule({
            region: this.layout.labelList,
            hideEnglishLabel: this.hideEnglishLabel,
            namespace: this.name,
          }),
        );
        this._showSubViews(this.topicLabels, false);
      } else {
        this._prepareTouchPointLabels(this.touchPointLabels);
      }
    },
    fillTouchPointSelect: function () {
      const kbTouchpoints = Messenger.reqres.request("kb:touchpoints");
      const touchpointNames = [];
      const touchpoints = {};
      kbTouchpoints.onSynced(function () {
        this.layout.clearTouchPoints();
        let i;
        for (i = 0; i < kbTouchpoints.length; i++) {
          if (kbTouchpoints.at(i).get("touchPointId") === this.defaultTouchpointId) {
            touchpointNames.unshift(kbTouchpoints.at(i).get("touchPoint"));
          } else {
            touchpointNames[i] = kbTouchpoints.at(i).get("touchPoint");
          }
          const keyName = kbTouchpoints.at(i).get("touchPoint");
          touchpoints[keyName] = kbTouchpoints.at(i).get("touchPointId");
        }
        touchpoints[this.standardTouchPointName] = this.standardTouchpointId;
        this.touchpointMap = touchpoints;
        //sort the array in alphabetical order, but first selected value should always be default
        this.defaultTouchpoint = touchpointNames[0];
        touchpointNames.splice(0, 1);
        touchpointNames.sort();
        touchpointNames.unshift(this.defaultTouchpoint);
        touchpointNames.unshift(this.standardTouchPointName);
        for (i = 0; i < touchpointNames.length; i++) {
          this.layout.addTouchPoint(touchpointNames[i], touchpoints[touchpointNames[i]]);
        }
      }, this);
    },

    removeEmptyTopicAndTouchpointLabels: async function () {
      const touchpointLabelsToRemove = [];
      const touchpointLabelsToDestroy = [];
      const promises = [];
      this.touchPointLabels.each(function (label) {
        //label summary id can be empty at the time of creating a topic and is available after the topic label is saved
        //in the process of saving a topic, topic label is saved first and then touchpoint label is updated with the
        //intent summary id of saved topic labels
        if (!label.get("summary") && !label.get("id")) {
          touchpointLabelsToRemove.push(label);
        }
        if (!label.get("summary") && label.get("id")) {
          touchpointLabelsToDestroy.push(label);
        }
      });
      this.touchPointLabels.remove(touchpointLabelsToRemove);

      touchpointLabelsToDestroy.forEach((model) => {
        promises.push(
          new Promise((resolve, reject) => {
            model.destroy({
              url: model.get("self").href,
              success: (_model, _response) => {
                resolve();
              },
              error: (_model, response) => reject(response),
            });
          }),
        );
      });

      const labelsToRemove = [];
      const labelsToDestroy = [];
      this.topicLabels.each(function (label) {
        if (!label.get("summary") && !label.get("summaryId")) {
          labelsToRemove.push(label);
        }
        if (!label.get("summary") && label.get("summaryId")) {
          labelsToDestroy.push(label);
        }
      });
      this.topicLabels.remove(labelsToRemove);

      labelsToDestroy.forEach((model) => {
        promises.push(
          new Promise((resolve, reject) => {
            model.destroy({
              url: model.get("self").href,
              success: () => {
                resolve();
              },
              error: (_model, response) => reject(response),
            });
          }),
        );
      });

      await Promise.all(promises);

      // need to manually sync with collection since we are overriding success callback in destroy call
      this.topicLabels.trigger("sync", this.topicLabels);
      this.touchPointLabels.trigger("sync", this.touchPointLabels);
    },

    _prepareTouchPointLabels: function (touchPointLabels) {
      const labelsToRemove = [];
      touchPointLabels.each(function (label) {
        label.selectedTouchPointId = this.selectedTouchPointId;
        label.set("intentId", this.activeIntentId);
        this.topicLabels.each(function (topicLabel) {
          if (topicLabel.get("languageId") === label.get("languageId")) {
            if (!topicLabel.get("summary")) {
              //if there is no topic label corresponding to this summary id, remove the touchpoint label
              labelsToRemove.push(label);
            } else {
              label.setTopicLabel(topicLabel);
              if (topicLabel.get("languageId") === 1 && this.hideEnglishLabel) {
                //"filter" out topic label for english
                label.hideModel = true;
              }
            }
          }
        }, this);
      }, this);

      touchPointLabels.remove(labelsToRemove);
      const touchpointEditable = Messenger.reqres.request("can:edit:touchPoint", {
        touchPointId: this.selectedTouchPointId,
      });
      const allowTouchpointView = Messenger.reqres.request("can:view:touchPoint", {
        touchPointId: this.selectedTouchPointId,
      });

      const allowTopicLabelsEdit = Messenger.reqres.request("can:edit:topicLabels");
      const allowTopicLabelsView = Messenger.reqres.request("can:view:topicLabels");

      let flag = false;
      this.topicLabels.each(function (topicLabel) {
        const languageId = topicLabel.get("languageId");
        const localeEditable = Messenger.reqres.request("can:edit:locale", {
          languageId: languageId,
        });
        const allowLocaleView = Messenger.reqres.request("can:view:locale", {
          languageId: languageId,
        });
        touchPointLabels.each(function (touchPointLabel) {
          if (
            languageId === touchPointLabel.get("languageId") &&
            touchPointLabel.get("touchpointId") === this.selectedTouchPointId
          ) {
            //no need to create a new touchpointLabel for this language
            flag = true;
            this.checkPermission(
              touchPointLabel,
              touchpointEditable,
              localeEditable,
              allowTopicLabelsEdit,
              allowTouchpointView,
              allowLocaleView,
              allowTopicLabelsView,
            );
            touchPointLabel.set({ topicSummary: topicLabel.get("summary") });
          }
        }, this);
        if (!flag) {
          const label = touchPointLabels.addLabel(this.selectedTouchPointId, topicLabel);
          label.set({ intentId: this.activeIntentId });
          label.selectedTouchPointId = this.selectedTouchPointId;
          label.set({ topicSummary: topicLabel.get("summary") });
          this.checkPermission(
            label,
            touchpointEditable,
            localeEditable,
            allowTopicLabelsEdit,
            allowTouchpointView,
            allowLocaleView,
            allowTopicLabelsView,
          );

          if (languageId === 1 && this.hideEnglishLabel) {
            //"filter" out topic label for english
            label.hideModel = true;
          }
        } else {
          flag = false;
        }
      }, this);

      //sort in order to rerender the sortableList
      touchPointLabels.sort();

      this.touchPointLabels = touchPointLabels;

      this._showTopicAndTouchPointLabelsView(this.topicLabels, this.touchPointLabels);
    },

    checkPermission: function (
      touchPointLabel,
      touchpointEditable,
      localeEditable,
      allowTopicLabelsEdit,
      allowTouchpointView,
      allowLocaleView,
      allowTopicLabelsView,
    ) {
      if (touchpointEditable && localeEditable && allowTopicLabelsEdit) {
        touchPointLabel.allowTouchPointLabelEdit = true;
      } else if (allowTopicLabelsEdit && allowLocaleView && allowTouchpointView) {
        touchPointLabel.allowTouchPointLabelEdit = false;
        touchPointLabel.allowTouchPointLabelView = true;
      } else if (allowTopicLabelsView && allowLocaleView && allowTouchpointView) {
        touchPointLabel.allowTouchPointLabelEdit = false;
        touchPointLabel.allowTouchPointLabelView = true;
      } else {
        touchPointLabel.allowTouchPointLabelEdit = false;
        touchPointLabel.allowTouchPointLabelEdit = false;
      }
      touchPointLabel.allowTopicLabelsView = !!allowTopicLabelsEdit;
      touchPointLabel.allowTopicLabelsView = !!allowTopicLabelsView;
    },
    _showSubViews: function (topicLabels, alreadyPrepared) {
      this.fillTouchPointSelect();
      this.labelList.show(topicLabels, alreadyPrepared);
    },

    _showTopicAndTouchPointLabelsView: function (topicLabels, touchPointLabels) {
      this.labelList = this.addSubModule(
        new TouchPointLabelListModule({ region: this.layout.labelList, namespace: this.name }),
      );
      this.labelList.show(topicLabels, touchPointLabels);
    },
  });
});
